import React, { useEffect } from "react";
import Cal, { getCalApi } from "@calcom/embed-react";
import clsx from "clsx";
import { CheckCircle2, CalendarClock } from "lucide-react";

export type BookingContext = {
  title: string;
  points: string[];
};

export type BookingSectionProps = {
  id: string;
  title: string;
  subtitle: string;
  calLink: string;
  calNamespace: string;
  context?: BookingContext;
  className?: string;
};

export const BookingSection: React.FC<BookingSectionProps> = ({
  id,
  title,
  subtitle,
  calLink,
  calNamespace,
  context,
  className,
}) => {
  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: calNamespace });
      cal("ui", {
        layout: "month_view",
        hideEventTypeDetails: false,
        theme: "light",
        styles: {
            branding: {
                brandColor: "#10b981" // Emerald-500
            }
        }
      });
    })();
  }, [calNamespace]);

  return (
    <section id={id} className={clsx("py-16 md:py-24 bg-slate-50 border-t-2 border-slate-200", className)}>
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 border-2 border-black mb-6 shadow-[4px_4px_0_0_rgba(0,0,0,1)]">
            <CalendarClock size={24} strokeWidth={2.5} />
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight">{title}</h2>
          <p className="mt-4 text-slate-600 text-lg md:text-xl font-medium">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Left Column: Context / Value Prop (Visible on Large Screens) */}
            {context && (
                <div className="lg:col-span-4 lg:sticky lg:top-24 space-y-6">
                    <div className="bg-white border-2 border-black rounded-2xl p-6 md:p-8 shadow-[6px_6px_0_0_rgba(0,0,0,1)]">
                        <h3 className="text-xl font-extrabold text-slate-900 mb-6 border-b-2 border-slate-100 pb-4">
                            {context.title}
                        </h3>
                        <ul className="space-y-4">
                            {context.points.map((point, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <CheckCircle2 className="h-6 w-6 text-emerald-500 shrink-0" strokeWidth={2.5} />
                                    <span className="text-slate-700 font-bold text-sm md:text-base leading-snug">
                                        {point}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 pt-6 border-t-2 border-slate-100">
                             <p className="text-xs font-bold text-slate-400 uppercase tracking-widest text-center">
                                No Commitment Required
                             </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Right Column: The Calendar */}
            <div className={clsx("w-full", context ? "lg:col-span-8" : "lg:col-span-12")}>
                <div className="bg-white border-2 border-black rounded-2xl overflow-hidden shadow-[8px_8px_0_0_rgba(0,0,0,1)] transition-transform hover:-translate-y-1">
                    {/* Decorative Header Bar */}
                    <div className="h-3 bg-emerald-500 w-full border-b-2 border-black" />
                    
                    <div className="p-2 md:p-4 bg-white min-h-[650px]">
                        <Cal
                            calLink={calLink}
                            namespace={calNamespace}
                            style={{ width: "100%", height: "100%", minHeight: 600 }}
                            config={{ layout: "month_view" }}
                        />
                    </div>
                </div>
            </div>

        </div>
      </div>
    </section>
  );
};